import { createTheme, type SxProps, type Theme } from '@mui/material/styles';
import type { } from '@mui/x-data-grid/themeAugmentation';

export const fillHeight: SxProps<Theme> = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0
};


export const theme = createTheme({
    typography: {
        fontFamily: 'Roboto, sans-serif',
    },
    colorSchemes: {
        light: {
            palette: {
                primaryContainer: "#D3E3FF",
                onPrimaryContainer: "#1F4876",
                primary: {
                    main: "#3A608F",
                    light: "#5479A9",   
                    dark: "#1F4876",                        
                    contrastText: "#FFFFFF",
                },

                secondary: {
                    main: "#545F70",
                    light: "#707784",
                    dark: "#3C4758",
                    contrastText: "#FFFFFF",
                },

                error: {
                    main: "#BA1A1A",
                    light: "#CF2C27",
                    dark: "#93000A",
                    contrastText: "#FFFFFF",
                },

                background: {
                    default: '#FFFFFF',
                    paper: '#F3F3F3',
                },

                text: {
                    primary: "#191C20",
                    secondary: "#43474E",
                },

                divider: "#C3C6CF",
                // DataGrid (v8) & MUI Table read their border color from this token, not `divider`.
                // Without it MUI lightens `divider` ~88%, making the grid lines nearly invisible.
                TableCell: { border: "#C3C6CF" },
            },
        },
        dark: {
            palette: {
                primaryContainer: "#1F4876",
                onPrimaryContainer: "#D3E3FF",
                primary: {
                    main: "#A4C9FE",
                    light: "#D3E3FF",
                    dark: "#1F4876",
                    contrastText: "#00315C",
                },

                secondary: {
                    main: "#BCC7DB",
                    light: "#D8E3F8",
                    dark: "#3C4758",
                    contrastText: "#263141",
                },

                error: {
                    main: "#FFB4AB",
                    dark: "#93000A",
                    contrastText: "#690005",
                },

                background: {
                    default: '#121212',
                    paper: '#1E1E1E',
                },

                text: {
                    primary: "#E1E2E8",
                    secondary: "#C3C6CF",
                },

                divider: "#43474E",
                TableCell: { border: "#43474E" },
            },
        },
    },
    components: {
        //MuiBox: {
        //    variants: [
        //        {
        //            props: { variant: 'flexColumnFill' },
        //            style: {
        //                flex: 1,
        //                display: 'flex',
        //                flexDirection: 'column',
        //                minWidth: 0,
        //                minHeight: 0,
        //                overflow: 'hidden',
        //            },
        //        },
        //    ],
        //},
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontSize: 16,
                },
                contained: {
                    fontWeight: 700,
                },
                outlined: {
                    fontWeight: 700,
                },
                text: {
                    fontWeight: 700,
                },
                textError: {
                    fontWeight: 700,
                }
            },
            defaultProps: {
                color: 'secondary',
                size: 'small',
            },
            variants: [
                {
                    props: { size: 'input' },
                    style: {
                        height: 40,
                        minHeight: 40
                    },
                },
            ],
        },
        MuiFormControl: {
            defaultProps: {
                size: 'small',
            },
            variants: [
                {
                    props: { size: 'input' },
                    style: {
                        height: 40,
                        display: 'flex',
                        justifyContent: 'center'
                    },
                },
            ],
        },
        MuiTextField: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiMenu: {
            styleOverrides: {
                list: ({ theme }) => ({
                    backgroundColor: theme.palette.background.paper,
                    //paddingTop: 0,
                    //paddingBottom: 0,
                }),
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: ({ theme }) => ({
                    backgroundColor: theme.palette.background.paper,
                }),
            },
        },
        MuiTab: {
            styleOverrides: {
                // Regular weight, not bold. The selected tab is already marked by its colour and indicator,
                // so weight was doing the job twice and made every tab strip shout.
                root: { fontSize: 16, fontWeight: 400 }
            }
        },
        MuiDataGrid: {
            styleOverrides: {
                // v8 derives grid borders from a very light token. Point the grid's own border
                // CSS variable (and the outer border color) at the divider so lines are visible.
                // Color-only — does not affect column widths, so no resize loop.
                root: ({ theme }) => ({
                    '--DataGrid-rowBorderColor': theme.palette.divider,
                    borderColor: theme.palette.divider,
                    // Partial vertical dividers between column headers (default color is the washed-out token)
                    '& .MuiDataGrid-columnSeparator': {
                        color: theme.palette.divider,
                    },
                    // Top border to separate the footer/pagination from the grid body
                    '& .MuiDataGrid-footerContainer': {
                        borderTop: `1px solid ${theme.palette.divider}`,
                    },
                }),
                columnHeaderTitle: { fontSize: 16, fontWeight: 700 },
                cell: {
                    // Hide the blue box around a selected cell
                    '&:focus': {
                        outline: 'none',
                    },
                    '&:focus-within': {
                        outline: 'none',
                    },
                },
            },
        },
    },
});
